import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { BaseInputProps } from './shared';
import { inputBaseClasses, inputErrorClasses } from './shared';

export interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'>,
    BaseInputProps {
  /** Date minimale (format YYYY-MM-DD) */
  min?: string;
  /** Date maximale (format YYYY-MM-DD) */
  max?: string;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      label,
      id,
      errors = [],
      helpText,
      containerClassName = '',
      className = '',
      min,
      max,
      disabled,
      readOnly,
      required,
      ...props
    },
    ref
  ) => {
    const hasErrors = errors.length > 0;
    const inputId = id || `date-input-${Math.random().toString(36).slice(2)}`;

    const inputClasses = [inputBaseClasses, hasErrors ? inputErrorClasses : '', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`space-y-token-xs ${containerClassName}`}>
        {label && (
          <label htmlFor={inputId} className="block type-caption-text font-medium text-ink">
            {label}
            {required && <span className="text-token-danger ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="date"
          min={min}
          max={max}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={hasErrors}
          aria-describedby={
            hasErrors ? `${inputId}-errors` : helpText ? `${inputId}-help` : undefined
          }
          aria-label={label || props['aria-label']}
          className={inputClasses}
          {...props}
        />
        {hasErrors && (
          <div id={`${inputId}-errors`} className="space-y-1" role="alert">
            {errors.map((error, index) => (
              <p
                key={index}
                className="type-caption-text text-token-danger flex items-center gap-1"
              >
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                {error}
              </p>
            ))}
          </div>
        )}
        {helpText && !hasErrors && (
          <p id={`${inputId}-help`} className="type-caption-text text-muted">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

DateInput.displayName = 'DateInput';
